import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FaArrowLeft, FaArrowRight, FaSearchMinus, FaSearchPlus } from 'react-icons/fa';
import { Document, Page, pdfjs } from 'react-pdf';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from 'rizzui';
import { toast } from 'sonner';
import fullLogo from '../../../assets/logo_completo.png';
import { useWindowDimensions } from "../../../hooks/useWindowDimensions";
import { menuRoutes } from "../../../router/menu";
import { ContractService } from "../../../services/contract/contractService";
import SignContractMobile from "./SignContractMobile";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PendingSignatureDocument = 'contract' | 'direct_debit_mandate';
const MIN_PREVIEW_ZOOM = 0.7;
const MAX_PREVIEW_ZOOM = 1.8;
const PREVIEW_ZOOM_STEP = 0.1;

const SignContract: FC = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const { hash } = useParams<{ hash: string }>();
    const { width } = useWindowDimensions();

    const [signatureHash, setSignatureHash] = useState<string | undefined>();
    const [showSignature, setShowSignature] = useState(false);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [contractPdfBlob, setContractPdfBlob] = useState<string | null>(null);
    const [directDebitPdfBlob, setDirectDebitPdfBlob] = useState<string | null>(null);
    const [contractData, setContractData] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [signingDocuments, setSigningDocuments] = useState(false);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [previewZoom, setPreviewZoom] = useState(1);
    const [activePreviewDocument, setActivePreviewDocument] = useState<PendingSignatureDocument>('contract');
    const [loadedPreviewByType, setLoadedPreviewByType] = useState<Record<PendingSignatureDocument, boolean>>({
        contract: false,
        direct_debit_mandate: false,
    });

    const contractSignatureRef = useRef<SignatureCanvas>(null);
    const directDebitSignatureRef = useRef<SignatureCanvas>(null);

    const pendingDocuments = useMemo<PendingSignatureDocument[]>(
        () => (contractData?.pendingSignatureDocuments ?? []) as PendingSignatureDocument[],
        [contractData]
    );
    const signatureSource = useMemo(() => {
        const source = new URLSearchParams(location.search).get('signatureSource')?.toLowerCase();
        return source === 'email' || source === 'sms' ? source : undefined;
    }, [location.search]);

    const pendingDocumentsCount = contractData?.pendingSignatureDocumentsCount ?? pendingDocuments.length;
    const hasContractPending = pendingDocuments.includes('contract');
    const hasDirectDebitPending = pendingDocuments.includes('direct_debit_mandate');

    const currentPdfBlob = activePreviewDocument === 'contract' ? contractPdfBlob : directDebitPdfBlob;
    const currentDocumentLoaded = loadedPreviewByType[activePreviewDocument];

    const goToSignedPage = (contract: any, contractIdFromResponse?: string) => {
        const contractId = contractIdFromResponse ?? contract?.id;

        if (!contractId) {
            navigate(menuRoutes.contract_public.error);
            return;
        }

        const requirements = (contract?.signatureRequirements ?? []) as PendingSignatureDocument[];
        const redirectToDirectDebitSigned = requirements.includes('direct_debit_mandate');

        if (redirectToDirectDebitSigned) {
            navigate(menuRoutes.direct_debit_mandate.signed.split(':')[0] + contractId);
            return;
        }

        navigate(menuRoutes.contract_public.signed.split(':')[0] + contractId);
    };

    const bufferToDataUrl = (buffer: ArrayBuffer): Promise<string> => {
        return new Promise((resolve, reject) => {
            const blob = new Blob([buffer], { type: 'application/pdf' });
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result ?? '') as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    const handleObtainContractInfo = async (currentHash: string) => {
        try {
            const response = await (await (new ContractService()).findContractByHash(currentHash, null)).getResponseData();

            if (!response?.data) {
                throw new Error('No se ha encontrado el contrato');
            }

            setContractData(response.data);
        } catch (error) {
            toast.error('Hubo un problema al obtener la información del contrato.');
            navigate(menuRoutes.contract_public.error);
        }
    };

    const handlePreviewDocument = async (documentType: PendingSignatureDocument) => {
        if (!signatureHash) {
            return;
        }

        if (documentType === 'contract' && contractPdfBlob) {
            return;
        }

        if (documentType === 'direct_debit_mandate' && directDebitPdfBlob) {
            return;
        }

        setIsLoadingPreview(true);

        try {
            const service = new ContractService();
            const response = documentType === 'contract'
                ? await service.visualizeContractPdf(signatureHash, undefined)
                : await service.visulizeDirectDebitPdf(signatureHash, undefined);

            if (!response?.data) {
                throw new Error('No se ha podido cargar el documento');
            }

            const dataUrl = await bufferToDataUrl(response.data);

            if (documentType === 'contract') {
                setContractPdfBlob(dataUrl);
            } else {
                setDirectDebitPdfBlob(dataUrl);
            }

            setLoadedPreviewByType((previous) => ({
                ...previous,
                [documentType]: true,
            }));
        } catch (error) {
            toast.error('No se ha podido cargar la previsualización del documento.');
            navigate(menuRoutes.contract_public.error);
        } finally {
            setIsLoadingPreview(false);
        }
    };

    const onDocumentLoadSuccess = ({ numPages: pages }: { numPages: number }) => {
        setNumPages(pages);
    };

    const handleSignPendingDocuments = async () => {
        if (!signatureHash) {
            toast.error('Hash inválido o nulo');
            return;
        }

        if (pendingDocumentsCount <= 0) {
            goToSignedPage(contractData);
            return;
        }

        const signatureContract = hasContractPending ? contractSignatureRef.current?.toDataURL() : undefined;
        const signatureDirectDebitMandate = hasDirectDebitPending ? directDebitSignatureRef.current?.toDataURL() : undefined;

        if (hasContractPending && contractSignatureRef.current?.isEmpty()) {
            toast.error('Debe firmar el contrato para continuar.');
            return;
        }

        if (hasDirectDebitPending && directDebitSignatureRef.current?.isEmpty()) {
            toast.error('Debe firmar el mandato de domiciliación para continuar.');
            return;
        }

        setIsLoading(true);
        setSigningDocuments(true);

        try {
            const response = await (await (new ContractService()).signContractDocuments(signatureHash, {
                signatureContract,
                signatureDirectDebitMandate,
                signatureSource,
            })).getResponseData();

            if (!response?.success) {
                toast.error(response?.message ?? 'No se han podido firmar los documentos');
                return;
            }

            toast.success(response?.message ?? 'Documentos firmados correctamente');
            goToSignedPage(contractData, response?.data?.contractId);
        } catch (error) {
            toast.error('Error al firmar los documentos');
        } finally {
            setIsLoading(false);
            setSigningDocuments(false);
        }
    };

    useEffect(() => {
        if (!hash) {
            navigate(menuRoutes.contract_public.error);
            return;
        }

        setSignatureHash(hash);
        handleObtainContractInfo(hash);
    }, [hash]);

    useEffect(() => {
        if (!contractData) {
            return;
        }

        if (pendingDocuments.length === 0) {
            goToSignedPage(contractData);
            return;
        }

        if (!pendingDocuments.includes(activePreviewDocument)) {
            setActivePreviewDocument(pendingDocuments[0]);
            setPageNumber(1);
        }
    }, [contractData, pendingDocuments.join(','), activePreviewDocument]);

    useEffect(() => {
        if (!pendingDocuments.includes(activePreviewDocument)) {
            return;
        }

        handlePreviewDocument(activePreviewDocument);
    }, [activePreviewDocument, signatureHash, pendingDocuments.join(',')]);

    return (
        <>
            <div className={`${width < 1024 ? 'block' : 'hidden'}`}>
                <SignContractMobile />
            </div>
            <div className={`${width >= 1024 ? 'block' : 'hidden'}`}>
                <div className="relative min-h-screen">
                    <div className="w-full h-[100vh] grid grid-cols-1 xl:grid-cols-3">
                        <div className="col-span-2 bg-gray-100 hidden lg:block">
                            <div className="flex flex-col justify-center items-center h-full">
                                {pendingDocuments.length > 0 && (
                                    <div className="mb-4 flex items-center gap-3">
                                        {hasContractPending && (
                                            <Button
                                                variant={activePreviewDocument === 'contract' ? 'solid' : 'outline'}
                                                onClick={() => {
                                                    setActivePreviewDocument('contract');
                                                    setPageNumber(1);
                                                }}
                                            >
                                                Ver contrato
                                            </Button>
                                        )}

                                        {hasDirectDebitPending && (
                                            <Button
                                                variant={activePreviewDocument === 'direct_debit_mandate' ? 'solid' : 'outline'}
                                                onClick={() => {
                                                    setActivePreviewDocument('direct_debit_mandate');
                                                    setPageNumber(1);
                                                }}
                                            >
                                                Ver domiciliación
                                            </Button>
                                        )}

                                        <div className="ml-2 flex items-center gap-1 rounded border border-gray-300 bg-white px-1 py-1">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="!px-2"
                                                onClick={() => setPreviewZoom((current) => Math.max(MIN_PREVIEW_ZOOM, +(current - PREVIEW_ZOOM_STEP).toFixed(2)))}
                                                disabled={previewZoom <= MIN_PREVIEW_ZOOM}
                                            >
                                                <FaSearchMinus className="w-3.5 h-3.5" />
                                            </Button>
                                            <span className="min-w-[58px] text-center text-sm text-gray-700">
                                                {Math.round(previewZoom * 100)}%
                                            </span>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="!px-2"
                                                onClick={() => setPreviewZoom((current) => Math.min(MAX_PREVIEW_ZOOM, +(current + PREVIEW_ZOOM_STEP).toFixed(2)))}
                                                disabled={previewZoom >= MAX_PREVIEW_ZOOM}
                                            >
                                                <FaSearchPlus className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {(currentPdfBlob && currentDocumentLoaded)
                                    ? (
                                        <>
                                            <Document
                                                file={currentPdfBlob}
                                                onLoadSuccess={onDocumentLoadSuccess}
                                                loading={<img
                                                    src="/media/loading-name-lg.svg"
                                                    alt="Cargando documento"
                                                    className="w-25 h-25"
                                                />}
                                            >
                                                {numPages && (
                                                    <Page
                                                        pageNumber={pageNumber}
                                                        scale={previewZoom}
                                                        renderMode="canvas"
                                                        renderTextLayer={false}
                                                        renderAnnotationLayer={false}
                                                    />
                                                )}
                                            </Document>

                                            {numPages && (
                                                <div className="mt-4 flex items-center gap-3">
                                                    <button
                                                        onClick={() => setPageNumber((prevPage) => Math.max(prevPage - 1, 1))}
                                                        disabled={pageNumber <= 1}
                                                        title="Página anterior"
                                                        className="text-xl p-2 rounded-full border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    >
                                                        <FaArrowLeft />
                                                    </button>
                                                    <p className="text-gray-700 min-w-[140px] text-center">
                                                        Página {pageNumber} de {numPages}
                                                    </p>
                                                    <button
                                                        onClick={() => setPageNumber((prevPage) => Math.min(prevPage + 1, numPages || 1))}
                                                        disabled={pageNumber >= (numPages || 1)}
                                                        title="Página siguiente"
                                                        className="text-xl p-2 rounded-full border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    >
                                                        <FaArrowRight />
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )
                                    : <img src="/media/loading-name-lg.svg" alt="Cargando documento" className="w-25 h-25" />
                                }
                            </div>
                        </div>

                        <div className="mx-auto w-full max-w-md rounded-xl px-4 py-9 flex flex-col justify-center">
                            <div className="flex flex-col items-center text-center mb-5">
                                <img src={fullLogo} width="70%" alt="Logo de Attiva" />
                            </div>

                            <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                                Tienes {pendingDocumentsCount} documento{pendingDocumentsCount === 1 ? '' : 's'} pendiente{pendingDocumentsCount === 1 ? '' : 's'} por firmar.
                            </div>

                            {!showSignature && (
                                <Button
                                    type="button"
                                    onClick={() => setShowSignature(true)}
                                    className="w-full mt-6"
                                    size="lg"
                                    disabled={pendingDocumentsCount <= 0}
                                >
                                    Firmar documentos pendientes
                                </Button>
                            )}

                            {showSignature && !signingDocuments && (
                                <div className="space-y-5 mt-6">
                                    {hasContractPending && (
                                        <div className="border border-gray-300 rounded-md p-2">
                                            <p className="mb-2 text-sm font-medium text-gray-700">Firma del contrato</p>
                                            <SignatureCanvas
                                                ref={contractSignatureRef}
                                                canvasProps={{
                                                    width: 500,
                                                    height: 160,
                                                    className: 'signature-canvas',
                                                }}
                                            />
                                            <div className="mt-2 flex justify-end">
                                                <Button type="button" onClick={() => contractSignatureRef.current?.clear()} variant="outline">
                                                    Limpiar
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {hasDirectDebitPending && (
                                        <div className="border border-gray-300 rounded-md p-2">
                                            <p className="mb-2 text-sm font-medium text-gray-700">Firma del mandato de domiciliación</p>
                                            <SignatureCanvas
                                                ref={directDebitSignatureRef}
                                                canvasProps={{
                                                    width: 500,
                                                    height: 160,
                                                    className: 'signature-canvas',
                                                }}
                                            />
                                            <div className="mt-2 flex justify-end">
                                                <Button type="button" onClick={() => directDebitSignatureRef.current?.clear()} variant="outline">
                                                    Limpiar
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    <Button
                                        type="button"
                                        onClick={handleSignPendingDocuments}
                                        isLoading={isLoading}
                                        disabled={isLoading}
                                        className="w-full"
                                    >
                                        Firmar y enviar documentos
                                    </Button>
                                </div>
                            )}

                            {signingDocuments && (
                                <div className="flex flex-col justify-center items-center text-center">
                                    <DotLottieReact
                                        src="https://lottie.host/e7d7459a-de1c-4374-9631-3e03d9d81b11/S0RljqPaCz.lottie"
                                        loop={false}
                                        autoplay
                                        speed={0.6}
                                        className="mb-4"
                                    />
                                    <p className="text-gray-600">Firmando tus documentos...</p>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SignContract;
